import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import { BlocksUtilityNavItem } from '../utility-nav-item';
import styles from './utility-nav.styles';

/**
 * @deprecated - This component `cat-utility-nav` has been deprecated and the updated version will be available with name `cat-utility-nav-labs`. New component will be found under labs folder from Minor Release 3.10.0, `cat-utility-nav` will be removed in 5.0 major release.
 * @slot default - The utility nav items
 * @csspart base - The component's base wrapper.
 * @csspart link - The component's link wrapper.
 * @csspart part-default - The component's default slot.
 */
export class BlocksUtilityNav extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Aria label attribute
   * - Provides an accessible name for the nav element
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * Provides an option for utility nav item to use 100% available space
   */
  @property({ type: Boolean })
  fullWidth = false;

  /**
   * Too keep current focused element.
   */
  private focusedElement: BlocksUtilityNavItem | null = null;

  /**
   * **Query the primaryNavItems from light dom**
   */
  private get utilityNavItems(): BlocksUtilityNavItem[] {
    return Array.from(this.querySelectorAll('cat-utility-nav-item'));
  }

  /**
   * First updated lifecycle when component is first updated on page
   * 1.Focus on the first element then should focus on the next focusable element after nav in the second tab press.
   */
  protected async firstUpdated() {
    this.updateComplete.then(() => {
      /*1*/
      const items = this.utilityNavItems;
      items.length && this.focusContent(items[0], items, false);
    });
  }

  /**
   * method is used to check any item in utility nav is active.
   */
  private hasActiveItem(items: BlocksUtilityNavItem[]): boolean {
    return items.some((item) => item.dropdown && item.isActive);
  }

  /**
   * method is used on keydown actions.
   * @param kbEvent
   * 1.If any active item is there with dropdown then focus will be shifted to active item and child.
   * 2.DOWN_ARROW,RIGHT_ARROW: Moves focus to the next item in the navigation.
   * 3.UP_ARROW,LEFT_ARROW: Moves focus to the previous item in the navigation.
   */
  private handleOnKeyDown(kbEvent: KeyboardEvent) {
    const items = this.utilityNavItems;
    const isActiveItem: boolean = this.hasActiveItem(items);
    if (isActiveItem) {
      /*1*/
      return;
    }

    if (kbEvent.key === KEYS.RIGHT_ARROW) {
      /*2*/
      kbEvent.preventDefault();
      this.moveFocusToNextItem(items);
    } else if (kbEvent.key === KEYS.LEFT_ARROW) {
      /*3*/
      kbEvent.preventDefault();
      this.moveFocusToPreviousItem(items);
    }
  }

  /**
   * method is used to focus next item on right arrow click
   * @param items
   */
  private moveFocusToNextItem(items: BlocksUtilityNavItem[]) {
    if (!this.focusedElement) {
      return;
    }
    let currentIndex = Array.from(items).indexOf(this.focusedElement);
    const nextIndex = (currentIndex + 1) % items.length;
    this.focusContent(items[nextIndex], items, true);
  }

  /**
   * method is used to focus previous item on left arrow click
   * @param items
   */
  private moveFocusToPreviousItem(items: BlocksUtilityNavItem[]) {
    if (!this.focusedElement) {
      return;
    }
    const currentIndex = Array.from(items).indexOf(this.focusedElement);
    const prevIndex = (currentIndex - 1 + items.length) % items.length;
    this.focusContent(items[prevIndex], items, true);
  }

  /**
   * Sets the tabindex and manages focus for utility navigation items.
   *
   * @param item - The currently selected navigation item.
   * @param navItems - List of all navigation items.
   * @param setFocus - If true, the selected item will receive focus.
   * 1.If the current item matches the selected item, make it focusable
   * 2.If setFocus is true, move focus to the selected item
   */
  private focusContent(item: HTMLElement, navItems: BlocksUtilityNavItem[], setFocus?: boolean) {
    navItems.forEach((navItem) => {
      const navItemElement = navItem.shadowRoot?.querySelector('.cat-c-utility-nav__link');
      if (navItem === item) {
        /*1*/
        navItemElement?.setAttribute('tabindex', '0'); // Enable keyboard focus
        this.focusedElement = navItem; // Store the focused element reference

        if (setFocus) {
          /*2*/
          navItem.focus();
        }
      } else {
        navItemElement?.setAttribute('tabindex', '-1');
      }
    });
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-utility-nav', {
      'cat-c-utility-nav--full-width': this.fullWidth === true
    });

    return html`
      <nav part="base" class="${componentClassName}" aria-label=${this.arialabel ? this.arialabel : 'Utility'} @keydown=${this.handleOnKeyDown}>
        <ul part="link" role="menubar" class="cat-c-utility-nav__list">
          <slot part="part-default"></slot>
        </ul>
      </nav>
    `;
  }
}

if (customElements.get('cat-utility-nav') === undefined) {
  customElements.define('cat-utility-nav', BlocksUtilityNav);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-utility-nav': BlocksUtilityNav;
  }
}
